import { useEffect, useRef, useState, useCallback } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

const PORTRAIT_MQ = "(max-width: 767px)";
const FADE_MS = 900;
const SWITCH_BEFORE_END_S = 1.0;

/**
 * Section-filling background that plays a playlist of slow cinematic clips,
 * cross-fading to the next clip when one ends, and drifts with the page
 * scroll (parallax) like the other section backdrops. Scoped to its parent
 * via clip-path so the video is only visible inside the hero wrapper.
 */
export const PageVideoBg = ({ clips, speed = 7, children }) => {
  const wrapRef = useRef(null);
  const layerRefs = [useRef(null), useRef(null)];
  const [isPortrait, setIsPortrait] = useState(() => window.matchMedia(PORTRAIT_MQ).matches);
  const [active, setActive] = useState(0);
  const [layerSrc, setLayerSrc] = useState([0, 1]);
  const playingIdx = useRef(0);
  const switching = useRef(false);

  const { scrollYProgress } = useScroll({ target: wrapRef, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [`${speed}%`, `-${speed}%`]);

  const list = clips.map((c) => (isPortrait ? c.portrait : c.landscape));
  const posters = clips.map((c) => (isPortrait ? c.posterPortrait : c.posterLandscape));
  const single = list.length < 2;

  useEffect(() => {
    const mq = window.matchMedia(PORTRAIT_MQ);
    const onChange = (e) => setIsPortrait(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  useEffect(() => {
    const el = layerRefs[0].current;
    if (!el) return;
    el.muted = true;
    const tryPlay = () => el.play().catch(() => {});
    tryPlay();
    document.addEventListener("touchstart", tryPlay, { once: true, passive: true });
    return () => document.removeEventListener("touchstart", tryPlay);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPortrait]);

  const onTimeUpdate = useCallback(
    (layer) => {
      if (single || layer !== active || switching.current) return;
      const v = layerRefs[layer].current;
      if (!v?.duration || v.duration - v.currentTime > SWITCH_BEFORE_END_S) return;
      switching.current = true;
      const other = 1 - layer;
      const ov = layerRefs[other].current;
      if (ov) {
        ov.currentTime = 0;
        ov.play().catch(() => {});
      }
      setActive(other);
      setTimeout(() => {
        playingIdx.current = (playingIdx.current + 1) % list.length;
        const upcoming = (playingIdx.current + 1) % list.length;
        setLayerSrc((s) => (layer === 0 ? [upcoming, s[1]] : [s[0], upcoming]));
        v.pause();
        switching.current = false;
      }, FADE_MS + 200);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [active, single, list.length]
  );

  return (
    <div ref={wrapRef} className="absolute inset-0 z-0" style={{ clipPath: "inset(0)" }} aria-hidden="true" data-testid="page-video-bg">
      <div className="absolute inset-0 bg-[#0A0A0A] overflow-hidden">
        <motion.div style={{ y }} className="absolute -inset-[8%] will-change-transform">
          <div className="absolute inset-0 handheld-drift">
          {[0, 1].map((layer) => {
            const idx = layerSrc[layer] % list.length;
            if (single && layer === 1) return null;
            return (
              <video
                key={layer}
                ref={layerRefs[layer]}
                className="absolute inset-0 w-full h-full object-cover"
                style={{ opacity: active === layer ? 1 : 0, transition: `opacity ${FADE_MS}ms ease-in-out` }}
                src={list[idx]}
                poster={layer === 0 ? posters[idx] : undefined}
                preload="auto"
                autoPlay={layer === 0}
                muted
                loop={single}
                playsInline
                webkit-playsinline="true"
                disablePictureInPicture
                onTimeUpdate={() => onTimeUpdate(layer)}
                data-testid={`page-bg-video-${layer}`}
              />
            );
          })}
          </div>
        </motion.div>
        {children}
      </div>
    </div>
  );
};

export default PageVideoBg;
